import React from "react";

interface ViewApplicationProps {
  title: string;
  description: string;
  subtitle: string;
  applicationType?: string;
  applicationStatus?: string;
  userProfile: {
    name: string;
    imageUrl: string;
  };
  basicTable: {
    columns: {
      Header?: string;
      accessor: string;
      name?: string;
    }[];
    data: {
      [key: string]: string | number;
    }[];
  };
  contactTable: {
    columns: {
      Header?: string;
      accessor: string;
      name?: string;
    }[];
    data: {
      [key: string]: string | number;
    }[];
  };
  paymentTable: {
    columns: {
      Header?: string;
      accessor: string;
      name?: string;
    }[];
    data: {
      [key: string]: string | number;
    }[];
  };
  educationTable: {
    columns: {
      Header?: string;
      accessor: string;
      name?: string;
    }[];
    data: {
      [key: string]: string | number;
    }[];
  };
  guarantorTable: {
    columns: {
      Header?: string;
      accessor: string;
      name?: string;
    }[];
    data: {
      [key: string]: string | number;
    }[];
  };
  docTable: {
    columns: {
      Header?: string;
      accessor: string;
      name?: string;
    }[];
    data: {
      [key: string]: string | number;
    }[];
  };
}

const ViewApplication: React.FC<ViewApplicationProps> = ({
  description,
  subtitle,
  basicTable,
  contactTable,
  paymentTable,
  educationTable,
  guarantorTable,
  docTable,
  applicationStatus,
  applicationType
}) => {

  // Helper to determine the border color based on status
  const getStatusBorderColor = (status: string | undefined) => {
    switch (status?.toLowerCase()) {
      case "approved":
        return "green-500"; // Green border for approved
      case "rejected":
        return "red-500"; // Red border for rejected
      default:
        return "yellow-500"; // Yellow border for other statuses
    }
  };

  return (
    <div className="flex h-full w-full">
      <div className="bg-[#F3F6FF] py-8 items-center w-full">
        <div className={`border-t-2  border-${getStatusBorderColor(applicationStatus)} bg-white shadow rounded-[8px] md:py-[30px] p-4 md:px-[50px] mb-[2px] flex items-center md:border-none`}>
          <div className="flex-1 flex-col">
            <h2 className="text-[24px] font-[600]">{description}</h2>
            <div className="flex flex-row gap-x-2 mt-[10px]">
              <p className="text-black text-[15px] font-[400] text-center">
                {subtitle}
              </p>
              <p className="text-black rounded-[6px] text-[15px] text-center font-[500] border border-[#CCCCCC] px-3 py-[1px]">
                {applicationType}
              </p>
            </div>
          </div>
          <div className="hidden flex flex-col items-center ml-8 py-[15px] md:block">
            <div className="flex flex-col items-center">
              <span className={`rounded-t-[8px] w-full h-[9px] mt-[10px] border bg-${getStatusBorderColor(applicationStatus)}`}></span>
              <p className="text-[16px] font-[600] text-center shadow rounded-b-[8px] px-[20px] py-[10px] h-[50px]">
                {applicationStatus}
              </p>
            </div>
          </div>
        </div>

        {/* Basic Info Table */}
        <div className="bg-white shadow rounded-[8px] p-4 md:py-[20px] md:px-[60px] mt-8">
          <div className="w-full">
            <div className="w-full flex flex-row justify-between items-center mb-6">
              <h2 className="text-[24px] font-[600] mb-[2px]">Basic Info</h2>
            </div>
          </div>
          <table className="w-full">
            <thead>
              <tr>
                {basicTable.columns.map((column, index) => (
                  <th key={index} className="text-left">
                    {column.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {basicTable.data.map((row, index) => (
                <React.Fragment key={index}>
                  <tr className="flex items-center">
                    <td className="flex-1 text-left">
                      {row[basicTable.columns[0].accessor]}
                    </td>
                    <td className="flex-1 text-center">
                      {row[basicTable.columns[1].accessor]}
                    </td>
                  </tr>
                  {index !== basicTable.data.length - 1 && (
                    <tr>
                      <td colSpan={4}>
                        <hr className="border-[1px] border-gray-200 my-3" />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

          <div className="bg-white shadow rounded-[8px] p-4 md:py-[20px] md:px-[60px] mt-8">
            <div className="w-full">
              <div className="w-full flex flex-row justify-between items-center mb-6">
                <h2 className="text-[24px] font-[600] mb-[2px]">
                  Contact Info
                </h2>
              </div>
            </div>
            <table className="w-full">
              <thead>
                <tr>
                  {contactTable.columns.map((column, index) => (
                    <th key={index} className="text-left">
                      {column.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {contactTable.data.map((row, index) => (
                  <React.Fragment key={index}>
                    <tr className="flex items-center">
                      <td className="flex-1 text-left">
                        {row[contactTable.columns[0].accessor]}
                      </td>
                      <td className="flex-1 text-center">
                        {row[contactTable.columns[1].accessor]}
                      </td>
                      
                    </tr>
                    {index !== contactTable.data.length - 1 && (
                      <tr>
                        <td colSpan={4}>
                          <hr className="border-[1px] border-gray-200 my-3" />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white shadow rounded-[8px] p-4 md:py-[20px] md:px-[60px] mt-8">
            <div className="w-full">
              <div className="w-full flex flex-row justify-between items-center mb-6">
                <h2 className="text-[24px] font-[600] mb-[2px]">
                  Payment Info
                </h2>
              </div>
            </div>
            <table className="w-full">
              <thead>
                <tr>
                  {paymentTable.columns.map((column, index) => (
                    <th key={index} className="text-left">
                      {column.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paymentTable.data.map((row, index) => (
                  <React.Fragment key={index}>
                    <tr className="flex items-center">
                      <td className="flex-1 text-left">
                        {row[paymentTable.columns[0].accessor]}
                      </td>
                      <td className="flex-1 text-center">
                        {row[paymentTable.columns[1].accessor]}
                      </td>
                      
                    </tr>
                    {index !== paymentTable.data.length - 1 && (
                      <tr>
                        <td colSpan={4}>
                          <hr className="border-[1px] border-gray-200 my-3" />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white shadow rounded-[8px] p-4 md:py-[20px] md:px-[60px] mt-8">
            <div className="w-full">
              <div className="w-full flex flex-row justify-between items-center mb-6">
                <h2 className="text-[24px] font-[600] mb-[2px]">
                  Educational Background
                </h2>
              </div>
            </div>
            <table className="w-full">
              <thead>
                <tr>
                  {educationTable.columns.map((column, index) => (
                    <th key={index} className="text-left">
                      {column.name}
                    </th>
                  ))}
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {educationTable.data.map((row, index) => (
                  <React.Fragment key={index}>
                    <tr className="flex items-center">
                      <td className="flex-1 text-left">
                        {row[educationTable.columns[0].accessor]}
                      </td>
                      <td className="flex-1 text-center">
                        {row[educationTable.columns[1].accessor]}
                      </td>
                      
                    </tr>
                    {index !== educationTable.data.length - 1 && (
                      <tr>
                        <td colSpan={4}>
                          <hr className="border-[1px] border-gray-200 my-3" />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
            <p className="italic text-[15px] text-right">
              Highest Education Level: Undergrad
            </p>
          </div>

          <div className="bg-white shadow rounded-[8px] p-4 md:py-[20px] md:px-[60px] mt-8">
            <div className="w-full">
              <div className="w-full flex flex-row justify-between items-center mb-6">
                <h2 className="text-[24px] font-[600] mb-[2px]">Guarantors</h2>
              </div>
            </div>
            <table className="w-full">
              <thead>
                <tr>
                  {guarantorTable.columns.map((column, index) => (
                    <th key={index} className="text-left">
                      {column.name}
                    </th>
                  ))}
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {guarantorTable.data.map((row, index) => (
                  <React.Fragment key={index}>
                    <tr className="flex items-center">
                      <td className="flex-1 text-left">
                        {row[guarantorTable.columns[0].accessor]}
                      </td>
                      <td className="flex-1 text-center">
                        {row[guarantorTable.columns[1].accessor]}
                      </td>
                      
                    </tr>
                    {index !== guarantorTable.data.length - 1 && (
                      <tr>
                        <td colSpan={4}>
                          <hr className="border-[1px] border-gray-200 my-3" />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white shadow rounded-[8px] p-4 md:py-[20px] md:px-[60px] mt-8">
            <div className="w-full">
              <div className="w-full flex flex-row justify-between items-center mb-6">
                <h2 className="text-[24px] font-[600] mb-[2px]">
                  Uploaded Documents
                </h2>
              </div>
            </div>
            <table className="w-full">
              <thead>
                <tr>
                  {docTable.columns.map((column, index) => (
                    <th key={index} className="text-left">
                      {column.name}
                    </th>
                  ))}
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {docTable.data.map((row, index) => (
                  <React.Fragment key={index}>
                    <tr className="flex items-center">
                      <td className="flex-1 text-left">
                        {row[docTable.columns[0].accessor]}
                      </td>
                      <td className="flex-1 text-center">
                        {row[docTable.columns[1].accessor]}
                      </td>
                      
                    </tr>
                    {index !== docTable.data.length - 1 && (
                      <tr>
                        <td colSpan={4}>
                          <hr className="border-[1px] border-gray-200 my-3" />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
    </div>
  );
};

export default ViewApplication;
